// src/lib/supabase.js
import { createClient } from '@supabase/supabase-js'
import { createWrappedClient } from './supabaseClient'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Check if credentials are configured
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('⚠️ Supabase credentials not configured!');
  console.error('Create a .env.local file with:');
  console.error('VITE_SUPABASE_URL=your-url');
  console.error('VITE_SUPABASE_ANON_KEY=your-key');
  console.error('\nSee QUICK_START.md for setup instructions.');
}

// Create base client.
// Auth options are set explicitly (rather than relying on supabase-js defaults)
// so token-refresh and cross-tab session behaviour is pinned across library
// upgrades — see the prior supabase-js prod outage. These match the current
// defaults; the apiClient's 401 recovery depends on autoRefreshToken being on.
const baseClient = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })
  : null

// Export wrapped client with timeout and retry
export const supabase = baseClient ? createWrappedClient(baseClient) : null

// Auth helpers
export const authHelpers = {
  // Sign up with email.
  // `emailRedirectTo` overrides where Supabase's confirmation email lands —
  // used by invitation onboarding to route back to /invite/<token> after
  // the user clicks the email-confirm link.
  async signUp(email, password, userData = {}, { emailRedirectTo } = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData, // Will be stored in auth.users.raw_user_meta_data
        ...(emailRedirectTo ? { emailRedirectTo } : {})
      }
    })
    return { data, error }
  },

  // Sign in with email
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Sign in with Google. Optional `redirectTo` overrides the post-OAuth
  // landing URL so flows that need to return to a specific path (e.g.
  // invitation onboarding) can pass it through.
  async signInWithGoogle({ redirectTo } = {}) {
    // No calendar scope on login. It was `calendar.readonly` — do-nothing: the
    // provider_token is never captured, and calendar access is granted by the
    // separate connect flow (calendar.events). Requesting it here only added a
    // scope to the consent screen for no gain.
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo || `${window.location.origin}/auth/callback`
      }
    })
    return { data, error }
  },

  // Sign out THIS device only (scope: 'local'). Deliberately not 'global':
  // a global sign-out revokes every session on every device, which is what
  // killed an open tab's session mid-action on 2026-06-25 (the tab kept a
  // clock-valid token and 401'd). Local sign-out leaves other sessions alive.
  async signOut() {
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    return { error }
  },

  // Get current session
  async getSession() {
    const { data: { session } } = await supabase.auth.getSession()
    return session
  },

  // Get current user
  async getUser() {
    const { data: { user } } = await supabase.auth.getUser()
    return user
  },

  // Listen to auth changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}